import _ from "underscore";
import DriveView from "../DriveView";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { Dialog, removeAllDialogs } from "./framework/dialog";
import { handleApi } from "../utils/error";
import { getVersion } from "../utils/native/nativeContainer";
import { openExternalUrl } from "../utils/native/OpenExternalUrl";
import { AppType, getAppType, getConnectionType, isIphone } from "../utils/native/nativeHelpers";
import * as logger from "../utils/logger";
import { endLogoutDiagnostics, setLogoutDiagnosticsPhase, startLogoutDiagnostics } from "../utils/logoutRaceDiagnostics";
import { shouldAddExtraLogging } from "../utils/logger.internal";
import constants from "../constants";
import User from "../model/User";
import { clearCurrentVehicle, clearPreviousVehicle, getCurrentVehicle } from "../utils/device";
import { ICredentials, ICredentialsWithServer } from "../utils/credentialsStore";
import { CancelablePromise } from "../utils/cancelablePromise";
import { getServerDate } from "../utils/storage/date";
import Context from "../app.context";
import { getCurrentHostName, getHomeHash, isTestDomain } from "../utils/uri";
import { extend, hide, show } from "../utils/vanilla";
import { BigSpinner } from "../utils/spinner";
import { SyncType } from "../sync/sync.types";
import rison from "rison";
import { MobileActions } from "../store/mobile/actions";
import { DriverActionTypes, LoginRequiredStatus, PageLevel } from "../store/mobile/types";
import { IWorkflowState } from "./workflow";
import { DataActions } from "../store/data/actions";
import { addinsCache, downloadAddin, getAddinConfig, getExtendedAppState, getFullHash, IAddinMetadata, promisifyAddinShutdownMethod } from "../utils/addinHelper";
import { collectCustomAddins, customPageControlFunc } from "../utils/pageLoader";
import { WorkdayType } from "../store/data/types";
import { Device } from "../model/Device";
import toastr from "../utils/toastr";
import { getDeviceBySerialNumber } from "../services/ioxDataHandler";
import { clearTempAssetsFromState } from "./assets/helper";
import scheduler from "../utils/scheduler";
import DutyStatusLog from "../model/DutyStatusLog";
import pageHTML from "./login.tmpl";
import { unregisterServiceWorkers } from "../utils/serviceWorker";
import { isNetworkError } from "../utils/errorHandler/error.types";
import { DeviceMovementActions } from "../store/deviceMovement/actions";
import { FeatureFlagName } from "../utils/featureFlagName";
import { IIamSettings } from "../utils/auth/iamSettings";
import { IIamAppState, isIamCallback, isMyAdmin } from "../services/iamService";
import IamApi from "../utils/auth/iamApi";
import { IamDatabaseSelectionError, IamFlow, IamLoginCancelledError } from "../utils/auth/iamFlow";
import { shouldUseAppAuthIamLogin } from "../utils/auth/appAuthIamLogin";
import { ShipmentActions } from "../store/shipment/actions";
import { getDatabaseNameFromUri, IUrl } from "../utils/databaseNameParsingUtilities";
import { sanitizeUserName } from "../utils/users";
import { DriveAPICredentialsActions } from "../store/driveAPICredentials/actions";
import PerformanceProfilerFactory from "../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import { startBasicLoginProfiler, cancelBasicLoginProfiler, endBasicLoginProfiler } from "../modules/core/auth/services/basicLoginProfiler";
import {
    getUrlPrefix,
    checkIfRedirectIsRequired,
    redirectDatabaseIfNeeded,
    loginRedirect,
    removeNumberFromSubDomainPath,
    databaseRedirect,
    isNumberFromSubDomainPathNotAllowed
} from "../utils/redirectioner";
import { ISessionError } from "../utils/session";
import { clearLegacyLogoutStorage } from "../utils/legacyLogoutStorage";
import { registerPushTokenAfterLogin } from "../modules/core/auth/services/drivePushTokenService";
import { isQrLoginEnabled, isQrLoginIssuerSupported, QR_CLIENT_ID, QrLoginNotAllowedError } from "../modules/core/auth/helpers/qrLogin";
import {
    DeviceAuthError,
    DeviceAuthErrorCode,
    getDeviceAuthConfig,
    IDeviceAuthConfig,
    IDeviceAuthTokens,
    revokeDeviceAuthTokens
} from "../modules/core/auth/services/deviceAuthClient";
import { createDeviceAuthSession } from "../modules/core/auth/services/deviceAuthSession";
import { QrLoginPanel } from "../modules/core/auth/ui/qr/QrLoginPanel";
import { LoginErrors } from "../modules/core/auth/helpers/errors";
import { cleanupOldDrvfsFiles } from "../model/Image";
import { webViewLayer } from "../utils/webViewLayer";
import { promptPasswordUpdateIfRequired } from "../modules/core/auth/services/promptPasswordUpdateIfRequired";

const DRIVING_WITHOUT_LOGIN_WARNING_MESSAGE = "Cannot interact with the ELD while the vehicle is in motion.";
export const DB_INPUT_REQUIRED_MSG = "Database name is required";
export const USER_NOT_VERIFIED_VIA_IAM = "User has not been validated via IAM.";
export const USER_LOCKOUT_MSG = "Your account is temporarily locked due to multiple unsuccessful login attempts.";
export const EXTERNAL_AUTH_FAILED_MSG = "There was an issue with external authentication, please try again.";
const QR_NO_DATABASE_MSG = "We could not find a database for this account.";
const QR_NOT_ALLOWED_MSG = "QR sign-in isn't available for this account. Sign in with your username.";

const DRIVE_DEBUG_FLAG = "$drivedebug_";
enum DebugFlags {
    DebugModeOn = "debugmodeon",
    DebugModeOff = "debugmodeoff",
    NewLoginPage = "newloginpage"
}

export class AbortableToken {

    private isPromiseAborted = false;
    private onAbortCallback;

    abort() {
        this.isPromiseAborted = true;
        this.onAbortCallback && this.onAbortCallback();
    }

    isAborted() {
        return this.isPromiseAborted;
    }

    onAbort(onAbortCallback) {
        this.onAbortCallback = onAbortCallback;
    }
}

export enum StageTwoState {
    Checking = "Checking",
    GetPassword = "GetPassword"
}

interface ILoginState {
    isInProgress?: boolean;
    stageTwoState?: StageTwoState;
    progressText?: string;
    isCancelable?: boolean;
    isLogout?: boolean;
    error?: string;
    isExpiredSession?: boolean;
    username?: string;
    userPass?: string;
    database?: string;
    server?: string;
    viaLastDatabase?: string;
    showMore?: boolean;
    addCoDriver?: boolean;
    androidAppVersion?: string | null;
    appVersion?: string;
    loginDifferentDbError?: boolean;
    databaseRequired?: boolean; // If `authenticate` fails with DatabaseRequired
    redirect?: string;
}

interface IParsedAddin {
    addinName: string;
    meta: IAddinMetadata;
}

export interface ILoginUrlState {
    credentials?: ICredentials;
    server?: string;
    doLogout?: boolean;
    switchUser?: string;
    loggingOut?: boolean;
    reselectVehicle?: boolean;
    addCoDriver?: boolean;
    loggingIn?: boolean;
    next?: string;
    vehicleHint?: string;
    cleanUp?: boolean;
    errorMessage?: string;
    skipWorkflow?: boolean;
    iam?: boolean;
    redirect?: string;
    iamRedirect?: boolean;
    username?: string;
}

export default class LoginView extends DriveView<Backbone.Model> {
    protected strings = [
        "Your session has expired. You will need to log in again.",
        "Your session is invalid. Please log in again.",
        "Device could not be attached due to network issues.",
        "Device not found in this database. Please contact your administrator.",
        "The database is under maintenance. Please try again later.",
        "Unknown database.",
        "The Database name is required.",
        "Your account is temporarily locked due to multiple unsuccessful login attempts.",
        "Incorrect username or password.",
        "We're having some trouble reaching the server. Please try again later.",
        "Server not found.",
        "Maximum number of users logged in at once reached ({0}). Select the users whose sessions you want to end.",
        "Logged in since {0}",
        "Selected sessions ended",
        "Synchronizing your data...",
        "An error occurred during the log in process. Please try again."
    ];
    private readonly spinner = new BigSpinner();
    private readonly twoStageLoginSpinner = new BigSpinner({
        length: 7, // The length of each line
        radius: 7, // The radius of the inner circle
        width: 4, // The line thickness
        top: "18px" // Top position relative to parent in px
    });
    private lastState: ILoginState = {
        isInProgress: false,
        progressText: "",
        isCancelable: true,
        isLogout: false,
        error: "",
        isExpiredSession: false,
        username: "",
        userPass: "",
        database: "",
        server: "",
        viaLastDatabase: "",
        showMore: false,
        addCoDriver: false,
        androidAppVersion: getVersion(),
        appVersion: constants.APP_VERSION.toString()
    };
    private readonly template = _.template(pageHTML);
    private readonly isLoggingOut: boolean;
    private readonly loggingIn: boolean;
    private readonly isReselectVehicle: boolean;
    private readonly nextPage: string | undefined = undefined;
    private readonly vehicleHint: string | undefined = undefined;
    private readonly addCoDriver: boolean;
    private readonly skipWorkflow: boolean;
    private positionDialog: Dialog | null;
    private sessionUser: string | null = null;
    private inProgressLogin;
    private alreadyLoggedIn = false;
    private isAuthenticated = false;
    private viaText: string;
    private iamRedirect?: boolean;
    private iamRedirectUsername?: string;
    private readonly addins: any;
    private readonly redirect?: string;
    private qrLoginPanel?: QrLoginPanel;
    // Bumped to abandon work that is still running: a mount that is waiting for the server, or an approved QR login.
    private qrMountId = 0;
    private qrLoginRun = 0;
    // The newest completeQrLogin. Unlike qrLoginRun, Cancel does not move it: only a newer approval or removing the view
    // does, and then the older attempt must leave the page, the address and the panel to the newer one.
    private qrCompletion = 0;
    private qrConfigPromise?: Promise<IDeviceAuthConfig>;

    // eslint-disable-next-line complexity
    constructor(options) {
        super(options, {
            title: options && options.state && options.state.addCoDriver ? "Add co-driver" : "Login",
            activeSection: "home",
            pageLevel: options && options.state && options.state.addCoDriver ? PageLevel.TWO : PageLevel.ZERO
        });

        if (self !== top) {
            window.parent.postMessage({ type: "drive:login-ready", server: window.location.hostname }, "file:///");
        }

        dispatcher.trigger(DispatcherEvents.HideToastMessage);
        removeAllDialogs();

        let state: ILoginUrlState = this.options?.state || {};
        let currentUser = this.app.userService.getCurrentUser();

        if (state.iamRedirect && state.username) {
            this.iamRedirect = true;
            this.iamRedirectUsername = state.username;
            const databaseFromUri = getDatabaseNameFromUri(window.location);
            window.history.replaceState(null, document.title, `${ document.location.origin }${ databaseFromUri.length > 0 ? "/" + databaseFromUri : "" }/drive`);
        }

        if (state.doLogout) {
            this.setIsCancelable(false);
            this.showMessage("Logging out...", true);
            void clearLegacyLogoutStorage(this.app.api);
            this.showLoginForm();
        }

        this.isLoggingOut = !!state.loggingOut;
        this.isReselectVehicle = !!state.reselectVehicle;
        this.addCoDriver = !!state.addCoDriver;
        this.loggingIn = !!state.loggingIn;
        this.nextPage = state.next;
        this.vehicleHint = state.vehicleHint;
        this.addins = collectCustomAddins(this.app.databaseCache.getAddins(), this.app.userService.getCurrentUser());
        this.isAuthenticated = false;
        this.skipWorkflow = !!state.skipWorkflow;
        this.redirect = state.redirect;

        // Logging out locally
        if (this.isLoggingOut) {
            this.logout();
            return;
        }

        if (this.addCoDriver) {
            // Don't show the lock screen when adding a co-driver
            dispatcher.trigger(DispatcherEvents.LockscreenToggle, false);
        }

        this.listenTo(dispatcher, DispatcherEvents.ApiNeedsCredentials, (apiUserName: string, errorMessage?: string) => {
            if (this.lastState?.error) {
                return;
            }
            this.isAuthenticated = false;
            let currentState: ILoginUrlState = this.options?.state || {};
            let credentialsFromRemote = currentState && currentState.credentials;
            let error: string | undefined;
            let isExpiredSession = (this.loggingIn || !!currentState.switchUser) && !!currentUser;
            let isInvalidSession = !!(apiUserName && credentialsFromRemote && credentialsFromRemote.sessionId && credentialsFromRemote.userName.toLowerCase() === apiUserName.toLowerCase());

            let lastDatabase = this.getLastDatabase();
            let targetDatabase = credentialsFromRemote?.database;
            let loginDifferentDbError = !!currentUser && !!lastDatabase && !!targetDatabase && lastDatabase !== targetDatabase.toLowerCase();
            let username: string | undefined = undefined;
            if ((isExpiredSession || isInvalidSession) && apiUserName) {
                this.sessionUser = apiUserName;
                username = this.sessionUser;
            }

            if (loginDifferentDbError && currentUser) {
                this.sessionUser = currentUser?.name;
                username = apiUserName;
            }

            if (errorMessage) {
                error = errorMessage;
                this.updateState({
                    isInProgress: false,
                    error: error,
                    isExpiredSession,
                    loginDifferentDbError
                }, username);
            } else if (isExpiredSession) {
                this.app.translationService.getTranslator().translate<string>("Your session has expired. You will need to log in again.").then(translated => {
                    this.updateState({
                        isInProgress: false,
                        error: translated,
                        isExpiredSession,
                        loginDifferentDbError
                    }, username);
                });
            } else if (isInvalidSession) {
                this.app.translationService.getTranslator().translate<string>("Your session is invalid. Please log in again.").then(translated => {
                    this.updateState({
                        isInProgress: false,
                        error: translated,
                        isExpiredSession,
                        loginDifferentDbError
                    }, username);
                });
            }
        });

        // Switch user
        if (state.switchUser) {
            this.switchUser(state);
            return;
        }

        if (this.loggingIn) {
            this.setEnableSyncDelay(true);
        }

        // Redirected from another server or SSO
        if (state.credentials) {
            this.setCredentialsFromRemote(state);
            return;
        }

        if (state.iam || isIamCallback()) {
            const params = (new URL(location.href)).searchParams;
            const encodedUsername = params.get("username");
            const username = encodedUsername && decodeURIComponent(encodedUsername);
            const iamSettings = username && this.app.api.getDriverCredentials(username)?.iamSettings;

            if (iamSettings) {
                this.showMessage(this.getLoginAsMessage());
                this.iamDesktopLogin(iamSettings, state, username);
                return;
            }
        }

        this.storeUnsubscribe = this.app.observeStore(storeState => [storeState.data.driving], () => this.storeListener());
        this.storeListener();

        // Use credentials if we got 'em
        let tempContext: Context | null = null;
        if (this.addCoDriver) {
            // set previous user's language because we don't know CoDriver language yet
            let language = this.app.translationService.getLanguage();
            tempContext = this.app.manager.getTempContext();
            tempContext && tempContext.translationService.setLanguage(language);
        }
        this.app.context = tempContext || this.app.context;

        this.updateState({
            isExpiredSession: false,
            error: state.errorMessage || ""
        });
        this.setLoginMessage();

        void this.getSessionAndNavigateToNext();
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #login-more-toggle": this.showMoreFields,
            "submit #login-form": this.checkSamlLogin,
            "submit #login-form-auth": this.submitLoginForm,
            "click #login-session-expired-logout": this.expiredSessionLogout,
            "click #login-cancel": this.cancelWork,
            "click #login-auth-cancel": this.cancelWork,
            "click #toggle-visibility": this.showPassword,
            "keydown #toggle-visibility": this.showPassword,
            "click #login-forgot-password": this.forgotPassword
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    showImproperPermissionMessage(needsClearances: ISessionError, userName?: string) {
        const requiredPermissions = needsClearances.message?.map(message => {
            return typeof message === "string" ? message : message.join(" or ");
        });

        if (requiredPermissions?.length) {
            logger.error(logger.Source.USER, `User is missing required permissions: ${ requiredPermissions.join(", ") }`, userName);
        }

        let improperPermissionsDialog = new Dialog(
            "improperpermissions-dialog",
            `Please contact an administrator to provide permissions for${ requiredPermissions ? ": " + requiredPermissions.join(", ") : " the application" }`,
            this.translator,
            "Improper Permissions",
            [
                {
                    text: "OK",
                    id: "login-missing-permissions-ok",
                    callback: () => {
                        improperPermissionsDialog.remove();
                        this.showLoginForm();
                    }
                }
            ],
            {
                closable: false,
                showCloseIcon: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void improperPermissionsDialog.open();
    }

    setLoginMessage() {
        let currentUser = this.app.userService.getCurrentUser();

        if (this.loggingIn && currentUser) {
            let loginCurrentUser = new User(currentUser);
            this.showMessage(this.getLoginAsMessage(loginCurrentUser && loginCurrentUser.getFullName()));
        } else if (this.inProgressLogin) {
            this.showMessage("Logging in...");
        } else if (this.isLoggingOut) {
            this.showMessage("Logging out...");
        }

        this.setIsCancelable(true);
    }

    expiredSessionLogout() {
        clearTempAssetsFromState(this.app.store, this.app.userService);
        if (this.sessionUser) {
            this.app.manager.unregisterByUsername(this.sessionUser, true);
            this.app.userService.removeUser(this.sessionUser);
        }
        this.switchToNextUser(true);
    }

    async forgotPassword(e?: Event) {
        e && e.preventDefault();

        const handleResetPasswordLegacyFlow = () => {
            let port = window.location.port;
            let nativeUrl = `${ window.location.protocol }//${ window.location.hostname }${ port ? ":" + port : "" }/resetPassword.html`;
            openExternalUrl(nativeUrl, "_blank");
        };

        const serverVal: string = this.el.querySelector("#server")?.value.trim().toLowerCase() ?? "";
        const isUserProvidedServer = serverVal?.length > 0;
        const currentHostName = getCurrentHostName();
        let server = isUserProvidedServer ? serverVal : currentHostName;
        let database: string = this.el.querySelector("#database")?.value.trim().toLowerCase() || getDatabaseNameFromUri(window.location);
        const username: string = this.replaceConfusableUnicodeCharacters(this.el.querySelector("#username")?.value ?? "");

        try {
            const redirectUrlResult = await this.app.api.serverCall(server, "GetUserRedirectInformation", {
                database,
                userName: username,
                server: server,
                relayState: this.getRelayState()
            });
            if (redirectUrlResult.informationType === "IAM") {
                let iamSettings = {
                    iamProviderType: redirectUrlResult.iamProviderType,
                    loginUrl: redirectUrlResult.loginUrl,
                    clientId: redirectUrlResult.clientId,
                    language: this.app.translationService.getLanguage() || "en"
                };
                this.app.context.iamService.signInAndChangePassword(iamSettings, username);
            } else {
                handleResetPasswordLegacyFlow();
            }
        } catch (error) {
            if (error.message === DB_INPUT_REQUIRED_MSG) {
                this.updateState({
                    showMore: true,
                    databaseRequired: true,
                    stageTwoState: undefined,
                    error: !error.isAborted && error.message ? error.message : "",
                    isInProgress: false
                });
            } else {
                logger.error(logger.Source.ERROR, `Failed to reset password: ${ error.message }`);
                handleResetPasswordLegacyFlow();
            }
        }
    }

    cancelWork(e?: Event) {
        if (e) {
            e.preventDefault();
        }

        if (this.isLoggingOut) {
            this.cancelLogout();
        } else {
            this.cancelLogin();
        }

        return false;
    }

    checkIamRedirect() {
        if (this.iamRedirect && this.iamRedirectUsername) {
            const username = this.el.querySelector("#username");
            const database = this.el.querySelector("#database");
            const server = this.el.querySelector("#server");
            const submit = this.el.querySelector("#login-next");

            if (!username || !submit || !database) {
                return;
            }


            // simulate the user entering the username and hitting the submit button.
            // server value could be set to a different value from legacy login to another federation so we need to set it to the current
            username.value = this.iamRedirectUsername;
            server.value = window.location.host;

            // same thing with database issue with database, it could be set from legacy login to another federation
            // when we do an IAM redirect, we set the server in the url if user enter one
            // if not, we will set it to empty so it doesn't use the saved one.
            database.value = getDatabaseNameFromUri(window.location);

            // update the last database & server, so if the user click back button on IAM page
            // the value is using what the user enter and not what is stored
            const creds = {
                userName: this.iamRedirectUsername,
                database: database.value
            };
            this.setLastDatabase(creds);
            this.setLastServer(window.location.host);

            submit.click();

            this.iamRedirect = undefined;
            this.iamRedirectUsername = undefined;
        }
    }

    render() {
        let currentCredentials;
        let lastDatabase = this.getLastDatabase();
        let lastServer = this.getLastServer();

        if (this.addCoDriver) {
            currentCredentials = this.app.api.getCurrentCredentials();
            if (currentCredentials) {
                lastDatabase = currentCredentials.credentials.database;
                lastServer = currentCredentials.server;
            }
        }

        this.dispatchDriverActionNecessary(true);

        void this.translator.translate("Logging into {0}").then(via => {
            this.viaText = via;

            void this.display(
                this.template({
                    isIphone: isIphone(),
                    isAddCoDriver: this.addCoDriver,
                    isLoggingOut: this.isLoggingOut
                })
            ).then(() => {
                this.alreadyLoggedIn = !this.lastState.error && this.loggingIn && !!this.app.userService.getCurrentUser();

                this.updateState({
                    username: this.sessionUser || (!this.addCoDriver ? this.app.store.getState().data.lastUser : "") || undefined,
                    userPass: "",
                    database: lastDatabase || undefined,
                    server: lastServer || undefined,
                    viaLastDatabase: lastDatabase ? via.replace("{0}", lastDatabase) : "",
                    addCoDriver: this.addCoDriver
                });

                // we need to wait for the page to render before we can fetch the elements
                // this is the earliest place to do it
                this.checkIamRedirect();
                void this.mountQrLogin();
            });
        });
        return this;
    }


    showMoreFields(e: Event): boolean {
        e.preventDefault();

        if (this.addCoDriver) {
            return false;
        }

        let databaseInput = this.el.querySelector("#database");
        this.updateState({ showMore: true });
        databaseInput && databaseInput.focus();
        return false;
    }

    // For iOS, username have to replace smart (’), (‘) with (') and (“), (”) with (")
    replaceConfusableUnicodeCharacters = (input: string): string => {
        return input.trim().toLowerCase().replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, "\"");
    };

    getRelayState(): string {
        if (this.shouldLoginWithAS()) {
            return this.addCoDriver ? "DriveCoDriverAS" : "DriveAS";
        } else {
            return this.addCoDriver ? "DriveCoDriver" : "Drive";
        }
    }

    getChromeVersion() {
        let raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
        return raw ? parseInt(raw[2], 10) : -1;
    }

    getSafariVersion() {
        let raw = navigator.userAgent.match(/OS ((\d+_?){2,3})\s/);
        return raw ? parseInt(raw[1], 10) : -1;
    }

    checkSamlLogin(e: Event): CancelablePromise {
        e.preventDefault();
        // ios webview doesn't automatically trim whitespace
        const username: string = this.replaceConfusableUnicodeCharacters(this.el.querySelector("#username")?.value ?? "");
        let database: string = this.el.querySelector("#database")?.value.trim().toLowerCase() || this.getDatabaseNameFromUriAndLog();
        const serverVal: string = this.el.querySelector("#server")?.value.trim().toLowerCase() ?? "";
        const isUserProvidedServer = serverVal?.length > 0;
        const currentHostName = getCurrentHostName();
        let server = isUserProvidedServer ? serverVal : currentHostName;
        const expiredSession = this.lastState.error?.includes("Your session has expired") || this.lastState.isExpiredSession === true;
        const appType = getAppType();
        const chromeVersion = this.getChromeVersion();
        const safariVersion = this.getSafariVersion();

        if (this.wasDebugModeToggled()) {
            this.render();
            return CancelablePromise.resolve();
        }

        const continueToPassword = () => {
            this.updateState({
                error: "",
                isInProgress: false,
                stageTwoState: StageTwoState.GetPassword,
                username: username,
                viaLastDatabase: this.viaText && database && database.length > 0 ? this.viaText.replace("{0}", database) : ""
            });
        };

        if (this.lastState.databaseRequired && database.length === 0) {
            (this.el.querySelector("#database") as HTMLInputElement)?.focus();
            return CancelablePromise.resolve();
        }

        this.updateState({
            error: "",
            username,
            database,
            server
        });

        if (username) {
            this.updateState({ stageTwoState: StageTwoState.Checking });
            const handleSamlLogin = () => {
                const relayState = this.getRelayState();
                const internalRedirectUrl = `${ window.location.protocol }//${ currentHostName }/apiv1/RedirectToSamlLoginUrl?${ [
                    `database=${ encodeURIComponent(database || "\"\"") }`,
                    `userName=${ encodeURIComponent(username) }`,
                    isUserProvidedServer ? `server=${ server }` : "",
                    `relayState=${ relayState }`,
                    "redirect=true"
                ].join("&") }`;
                this.openSamlWindow(internalRedirectUrl, username, database, server);
            };
            const handleDatabaseRequiredError = (error) => {
                this.inProgressLogin = null;
                this.isAuthenticated = false;
                this.updateState({
                    showMore: true,
                    databaseRequired: true,
                    stageTwoState: undefined,
                    error: !error.isAborted && error.message ? error.message : "",
                    isInProgress: false
                });
            };

            const handleIamNotVerifiedError = () => {
                this.inProgressLogin = null;
                this.isAuthenticated = false;
                this.updateState({
                    showMore: true,
                    stageTwoState: undefined,
                    error: "Account not verified",
                    isInProgress: false
                });
            };

            let requestedServer = currentHostName;
            // isTestDomain is here because isValidServerName will return false for test domains (ex. localhost, 127.0.0.1)
            if (isUserProvidedServer && !isTestDomain(requestedServer)) {
                requestedServer = server;
            }

            // Determine if the user should be redirected to IAM / SAML or the Legacy login through the GetUserRedirectInformationDrive response
            return this.app.api
                .serverCall(requestedServer, "GetUserRedirectInformationDrive", {
                    database,
                    userName: username,
                    server: isUserProvidedServer ? server : undefined,
                    relayState: this.getRelayState()
                })
                .then(redirectUrlResult => {
                    let urlParts = getUrlPrefix(currentHostName.split("."));
                    const redirectUrlType = redirectUrlResult.informationType;
                    if (redirectUrlType === "SAML") {
                        handleSamlLogin();
                    } else if (redirectUrlType === "IAM" && ((!expiredSession && !this.addCoDriver) ||  urlParts.prefixNumber === "")) {
                        // This will prevent user who
                        // a) has expired session and is on a number subdomain from coming into the following method
                        // b) is a co-driver on a number subdomain from coming into the following method
                        // All other that comes in here are either
                        // A) is not expired user, and is first time logging in, therefore no data will be lost
                        // B) is an expired user but they are not on a number domain so no redirect will be done so no data will be lost

                        // prevent user with old drive version & old chrome version from going through IAM flow
                        if (appType === AppType.CordovaApp
                                || (chromeVersion < constants.IAM.MINIMUM_CHROME_VERSION && chromeVersion !== -1)
                                || (safariVersion < constants.IAM.MINIMUM_SAFARI_VERSION && safariVersion !== -1)) {
                            // eslint-disable-next-line max-len
                            logger.log(
                                logger.Source.IAM,
                                `User device does not meet the minimum require for logging in with IAM flow proceeding with legacy login. Device info: app type ${ appType }, Browser: ${ chromeVersion === -1 ? "Safari" : "Chrome" }, Browser version ${ chromeVersion === -1 ? safariVersion : chromeVersion }`,
                                username
                            );

                            continueToPassword();
                            return;
                        }

                        const iamProviderType = redirectUrlResult.iamProviderType;
                        const loginUrl = redirectUrlResult.loginUrl;
                        const clientId = redirectUrlResult.clientId;

                        // server contains numbered in the subdomain
                        // we would want to redirect the user to non-numbered
                        // unless it's on a support server (which is allowed via allowNumberedPrefix)
                        if (isNumberFromSubDomainPathNotAllowed(geotab.location.getVendorPrefix()) && urlParts.prefixNumber !== "") {
                            server = `${ urlParts.prefix }.${ urlParts.domain }.${ urlParts.zone }`;
                        }

                        if (server !== currentHostName && this.iamRedirect !== true) {
                            // check if we need to redirect the user
                            // the server is already validated above with isValidServerName
                            const url = `https://${ server }${ database.length > 0 ? `/${ database }` : "" }/drive/#login,(username:${ rison.encode(username) },iamRedirect:true)`;
                            window.location.replace(url);
                            return;
                        }

                        const iamSettings = { iamProviderType, loginUrl, clientId, language: this.app.translationService.getLanguage() || "en" };
                        const creds = { userName: username, database };
                        this.app.api.setUser(creds, server, iamSettings);
                        const appState: IIamAppState = { database, server, addCoDriver: this.addCoDriver };

                        if (!database.length) {
                            // Try to grab the DB name from the URL to pass along as a hint (even though this is a server-wide FF)
                            database = getDatabaseNameFromUri(window.location as IUrl, false, this.app.store);
                        }

                        const isAppAuthIamLoginPopupEnabled = shouldUseAppAuthIamLogin(
                            this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_APP_AUTH_IAM_LOGIN_POPUP)
                        );

                        new IamFlow(
                            this.app,
                            iamSettings,
                            username,
                            appState,
                            isAppAuthIamLoginPopupEnabled,
                            this.addLoginAuditLog.bind(this),
                            this.setCredentialsFromRemote.bind(this),
                            this.updateState.bind(this),
                            this.cancelLogin.bind(this),
                            ((cred: ICredentials) => this.setLastDatabase(cred)),
                            ((ser: string) => this.setLastServer(ser)),
                            (() => this.showMessage(this.getLoginAsMessage()))
                        )
                        .login(appState);
                    } else {
                        continueToPassword();
                    }
                })
                .catch(error => {
                    if (error.message === DB_INPUT_REQUIRED_MSG) {
                        handleDatabaseRequiredError(error);
                        return;
                    }
                    if (error.message === USER_NOT_VERIFIED_VIA_IAM) {
                        handleIamNotVerifiedError();
                        return;
                    }

                    logger.log(logger.Source.DEBUG, `GetUserRedirectInformationDrive call failed with error: ${ error.message }. Proceeding with legacy login.`, username);
                    continueToPassword();
                });
        } else {
            this.updateState({
                isInProgress: false,
                error: "Username is required."
            });
            return CancelablePromise.resolve();
        }
    }

    async submitLoginForm(e): Promise<void> {
        e.preventDefault();

        startBasicLoginProfiler();
        let loginStartTime = getServerDate().getTime();
        let password = this.el.querySelector("#password")?.value ?? "";
        let username = this.lastState.username;
        let database = this.lastState.database;
        let server = this.lastState.server;

        logger.log(logger.Source.USER, `Authenticating against '${ server }'`, username);

        this.showMessage("Logging in...");

        if (username && username.length) {
            this.app.store.dispatch(DataActions.setLastUser(username));
        }

        if (this.inProgressLogin) {
            this.inProgressLogin.abort();
            logger.log(logger.Source.USER, `Authentication attempt cancelled by '${ username }' (but is now logging in again)`, username);
        }

        this.inProgressLogin = new AbortableToken();
        try {
            await this.authentication(username, password, database, server, this.inProgressLogin);
            const session = await this.app.api.getSession(null, false);
            if (!session || !session.credentials) {
                logger.error(logger.Source.USER, "Failed to retrieve session after login", username);
                throw new Error(this.getString("An error occurred during the log in process. Please try again."));
            }
            if (await this.databaseRedirectIfRequired(session.credentials, session.server)) {
                return;
            }

            await this.getSessionAndNavigateToNext(session);

            this.inProgressLogin = null;

            if (server) {
                this.setLastServer(server);
            }

            this.addLoginDurationEvent(loginStartTime);
        } catch (error) {
            cancelBasicLoginProfiler();
            let showMore = this.lastState.showMore;
            let databaseRequired = this.lastState.databaseRequired;

            this.inProgressLogin = null;
            this.isAuthenticated = false;
            if (error.message === DB_INPUT_REQUIRED_MSG) {
                showMore = true;
                databaseRequired = true;
            }
            this.updateState({
                showMore,
                databaseRequired,
                stageTwoState: undefined,
                error: !error.isAborted && error.message ? error.message : "",
                isInProgress: false
            });
        }
    }

    iamDesktopLogin(iamSettings: IIamSettings, state: ILoginUrlState, username: string) {
        let database: string;
        let server: string;
        let iamJwt: string;

        this.app.context.iamService.init(iamSettings, username);
        let iamApi: IamApi = new IamApi(this.app.userService, this.app.store, "Login", username, false);
        this.showMessage(this.getLoginAsMessage());

        return this.app.context.iamService.handleRedirectAndGetUser(username)
            .then(user => {
                if (!user) {
                    throw new Error("Failed to get user");
                }

                const appState = user.state as IIamAppState;
                database = appState ? appState.database : "";
                server = (appState && appState.server) ? appState.server : getCurrentHostName();
                iamJwt = user.access_token;

                let credentialsFromRemote = { userName: username, database, iamJwt };
                iamApi.setUser(credentialsFromRemote, server, iamSettings);

                if (database && database.length > 0) {
                    return [database];
                }

                return iamApi.getUserDatabaseNames(username, iamJwt);
            })
            .then(databases => {
                // It should be impossible to throw here, because in CheckSamlLogin, the call to GetUserRedirectInformation should reutrn an
                // error if no database was provided, and there is not exactly one database. But throwing and logging errors just in case.
                if (databases && databases.length === 1) {
                    database = databases[0];
                } else if (databases.length === 0) {
                    throw new Error("Failed to get any database for user");
                } else {
                    throw new Error("Failed to find correct database for user");
                }
                return this.app.context.iamService.getClaim();
            })
            .then((claim) => {
                if (isMyAdmin(claim)) {
                    return iamApi.callUpsertUser(username, iamJwt, database);
                }
                return;
            })
            .then(() => {
                // check that the user logging in exists in the database
                return iamApi.checkUserExist(username, iamJwt, database);
            })
            .then((user) => {
                if (user && user.userAuthenticationType === "ServiceAccount") {
                    throw new Error("Service account not allowed to login");
                }
            })
            .then(() => {
                return this.app.context.iamService.getUsername() ?? username;
            })
            .then(userName => {
                if (!userName) {
                    throw new Error("Failed to get username");
                }

                let newState = state;
                newState.credentials = { userName, database, iamJwt };
                newState.server = server;

                this.addLoginAuditLog(iamApi, database);
                this.setLastDatabase(newState.credentials);
                this.setLastServer(server);

                this.app.store.dispatch(DataActions.setLastUser(userName));
                logger.log(logger.Source.IAM, "IAM Successfully signed in", userName);
                this.setCredentialsFromRemote(newState, iamSettings);
            })
            .catch((error) => {
                this.app.api.forget();
                this.app.context.iamService.handleSilentSignout(username);
                logger.error(logger.Source.IAM, `IAM Login error: ${ error.message }`, username);
                this.updateState({ isInProgress: false, error: EXTERNAL_AUTH_FAILED_MSG });
            });
    }

    remove() {
        this.qrMountId++;
        this.qrLoginRun++;
        this.qrCompletion++;
        this.qrLoginPanel?.destroy();
        this.qrLoginPanel = undefined;

        if (this.addCoDriver) {
            // We allow back button for co-drivers
            this.dispatchDriverActionNecessary(false);

            // App services were swapped out when attempting to add a coDriver,
            // this restores the previous app if we didn't log in with a co-driver (or just "re-affirms" the co-driver login)
            this.app.context = this.app.manager.getContext();
        }

        super.remove();
        return this;
    }

    /**
     * Used for testing in order to confirm LoginView's current state
     * @returns The current state of the LoginView
     */
    getCurrentState() {
        return this.lastState;
    }

    /**
     * Executes addin 'shutdown' functions during logout.
    */
    executeLogoutAddins() {
        if (this.app.userService.getActiveUserNames().length !== 1) {
            return CancelablePromise.resolve();
        }
        if (!this.addins) {
            return CancelablePromise.resolve();
        }

        let promises = [] as any;
        let appState = getExtendedAppState(this.app.store.getState().mobile, this.app.router);
        let parseAddinPromises = [] as CancelablePromise<IParsedAddin>[];

        for (let i = 0; i < this.addins.length; i++) {
            let addin = this.addins[i];

            if (!(addin.path && addin.path === "DriveAppLink/")) {
                continue;
            }

            let addinName = `${ addin.addinPath }${ addin.path }`;
            let addinConfig = getAddinConfig(addinName, this.app.databaseCache, this.app.userService.getCurrentUser());
            if (addinConfig && addinConfig.onShutdown) {
                // Addins are added to the cache currently one of two ways, either the function having been executed on startup or the addin's page having been opened.
                // Note: not sure what the purpose of the cache is, definitely candidate for removal as the entire addin content is downloaded on startup anyways?
                let cachedAddin = addinsCache.get(addinName);
                // func is null when the addin was preloaded but startup hasn't executed yet (deferred addin path)
                if (cachedAddin && cachedAddin.func) {
                    if (cachedAddin.func.shutdown instanceof Function) {
                        promises.push(
                            promisifyAddinShutdownMethod(
                                cachedAddin.func.shutdown,
                                this.app.addinService.getAddinApi(addinName),
                                appState,
                                addinName
                            )
                            .catch(err => {
                                handleApi(err, true);
                            })
                        );
                    }
                } else {
                    // If the addin hasn't been added to the cache add it to the list of addins whose content we want to download (for whatever reason)
                    parseAddinPromises.push(
                        downloadAddin(addinsCache.get(addinName), this.app.databaseCache, addinName, this.app.api, addinConfig)
                            .then(() => ({ addinName, meta: Object.freeze({ solutionId: addin.solutionId }) }))
                    );
                }
            }
        }

        if (parseAddinPromises.length) {
            // Download the addin content, then do the same check as above - if the addin has `onShutdown === true`, ensure that the addin's main function has
            // the `shutdown` property set, and if it is a Function wrap it in a promise (for safety) and add it to the list of shutdown functions to execute.
            return CancelablePromise.all(parseAddinPromises).then(addinContent => {
                for (let i = 0; i < addinContent.length; i++) {
                    let { addinName, meta } = addinContent[i];
                    let addinDefinition = customPageControlFunc(getFullHash(addinName));
                    let addinFunction = typeof addinDefinition === "function" && addinDefinition(this.app.api, null, meta);
                    if (addinFunction?.shutdown instanceof Function) {
                        promises.push(
                            promisifyAddinShutdownMethod(
                                addinFunction.shutdown,
                                this.app.addinService.getAddinApi(addinName),
                                appState,
                                addinName
                            ).catch(err => {
                                handleApi(err, true);
                            })
                        );
                    }
                }

                if (promises.length) {
                    // Run the shutdown functions. Races with the global timelimit for logout addin execution.
                    return CancelablePromise.race([
                        new CancelablePromise(resolve => setTimeout(resolve, constants.LOGOUT_ADDIN_EXECUTION_TIMELIMIT)), CancelablePromise.all(promises)
                    ]);
                }
                return CancelablePromise.resolve();
            }).catch((e: Error) => {
                handleApi(e, true);
                return CancelablePromise.resolve();
            });
        }

        if (promises.length) {
            // Run the shutdown functions. Races with the global timelimit for logout addin execution.
            return CancelablePromise.race([new CancelablePromise(resolve => setTimeout(resolve, constants.LOGOUT_ADDIN_EXECUTION_TIMELIMIT)), CancelablePromise.all(promises)]);
        }
        return CancelablePromise.resolve();
    }

    private readonly findById = (array, entityId) => {
        for (let i = 0; i < array.length; i++) {
            if (array[i] && array[i].id && array[i].id === entityId) {
                return array[i];
            }
        }
        return null;
    };

    private cancelLogin() {
        this.qrLoginRun++;
        this.isAuthenticated = false;
        this.updateState({
            isInProgress: false,
            stageTwoState: undefined
        });
        this.inProgressLogin && this.inProgressLogin.abort();
    }

    private cancelLogout(enableServices = true, isNetworkIssue = false) {
        // MYG-147181 / MYG-147182 (logging only): close the logout race diagnostics window.
        endLogoutDiagnostics(isNetworkIssue ? "failed" : "cancelled");
        let currentUser = this.app.userService.getCurrentUser();

        if (this.isLoggingOut && currentUser) {
            if (enableServices) {
                // Reenable services and server events.
                this.app.manager.enableSyncTasks(currentUser.name.toLowerCase());
            }

            if (isNetworkIssue) {
                setTimeout(() => {
                    let offlineLogoutDialog = new Dialog(
                        "offline-logout-dialog",
                        "Cannot log out due to limited or no internet connection. Try again later.",
                        this.translator,
                        "Disconnected: Logout Unavailable",
                        [{
                            id: "offline-logout-ok",
                            text: "OK",
                            callback: () => {
                                offlineLogoutDialog.remove();
                            }
                        }],
                        {
                            closable: false,
                            isNightMode: this.app.store.getState().data.nightMode
                        }
                    );
                    void offlineLogoutDialog.open();
                }, 500);
            }
            void this.navigateToNext();
        }
    }

    private async getSession(activeSession: ICredentialsWithServer | undefined = undefined) {
        const getSessionProfiler = PerformanceProfilerFactory.getInstance(MeasureName.getSession);
        getSessionProfiler?.start();
        try {
            const { credentials, server, iamSettings } = activeSession || await this.app.api.getSession(null, false);
            if (!credentials) {
                logger.error(logger.Source.USER, "getSession returned no credentials");
                return false;
            }
            // Skip redirect for cross-db codriver attempts - let loginSuccess handle validation
            let currentUser = this.app.userService.getCurrentUser();
            let lastDatabase = this.getLastDatabase();
            let lastDatabaseLower = lastDatabase?.toLowerCase();
            let incomingDatabase = credentials.database?.toLowerCase();
            let isCrossDbCodriver = currentUser && lastDatabaseLower && incomingDatabase && lastDatabaseLower !== incomingDatabase;

            let shouldRedirect = checkIfRedirectIsRequired(credentials.database, this.app.store);
            if (!isCrossDbCodriver && shouldRedirect) {
                // we are redirecting, no need to proceed further
                return undefined;
            }
            let lastServer = this.getLastServer();
            let targetDatabase = credentials.database.toLowerCase();

            if (lastDatabaseLower && lastDatabaseLower !== targetDatabase) {
                this.clearVehicles();
            }

            if (this.isAuthenticated) { // we don't need to call loginSuccess again, return here.
                return undefined;
            }
            return await this.loginSuccess(credentials, server, lastServer, targetDatabase, lastDatabase, iamSettings);
        } catch (error) {
            logger.error(logger.Source.USER, `Unexpected error in getSession: ${ error instanceof Error ? error.message : String(error) }`);
            throw error;
        } finally {
            getSessionProfiler?.end();
        }
    }

    private hasHosRulesets() {
        const currentRulesets = this.app.store.getState().data.ruleSets;
        return CancelablePromise.resolve(currentRulesets?.length > 0);
    }

    private async getSessionAndNavigateToNext(activeSession?: ICredentialsWithServer) {
        const isLoginSucceeded = await this.getSession(activeSession);
        return isLoginSucceeded;
    }

    private showPassword(e: Event) {
        if (e.type === "keydown" && (<KeyboardEvent>e).code !== "Enter" && (<KeyboardEvent>e).code !== "Space") {
            return;
        }
        let password = document.getElementById("password") as HTMLInputElement;
        let passwordHide = document.querySelector(".icon-eye-off") as HTMLInputElement;
        let passwordShow = document.querySelector(".icon-eye") as HTMLInputElement;
        let toggleVisibility = document.querySelector("#toggle-visibility") as HTMLInputElement;
        if (password.type === "password") {
            password.type = "text";
            passwordHide.style.display = "none";
            passwordShow.style.display = "block";
            toggleVisibility.setAttribute("aria-pressed", "true");
        } else {
            password.type = "password";
            passwordHide.style.display = "block";
            passwordShow.style.display = "none";
            toggleVisibility.setAttribute("aria-pressed", "false");
        }
    }

    private updateLoginState(oldState: ILoginState, newState: ILoginState, userEl: HTMLInputElement, passwordEl: HTMLInputElement, username?: string | null) {
        let step2UserEl = this.el.querySelector("#username-step2") as HTMLInputElement;
        let databaseEl = this.el.querySelector("#database") as HTMLInputElement;
        let serverEl = this.el.querySelector("#server") as HTMLInputElement;
        let setValueIfChanged = (el: HTMLInputElement, oldValue = "", newValue = "") => {
            if (el && (oldValue !== newValue || !el.value)) {
                el.value = newValue;
            }
        };

        setValueIfChanged(userEl, oldState.username, username ? username : newState.username);
        setValueIfChanged(step2UserEl, oldState.username, username ? username : newState.username);
        setValueIfChanged(passwordEl, oldState.userPass, newState.userPass);
        setValueIfChanged(databaseEl, oldState.database, newState.database);
        setValueIfChanged(serverEl, oldState.server, newState.server);
    }

    private updateState(updateState: ILoginState, username?: string | null) {
        let oldState = this.lastState;
        let newState = extend({}, this.lastState, updateState);

        let tryShowHideMoreToggle = (isShow: boolean) => {
            let showMoreButton = this.el.querySelector("#login-more-toggle");
            if (isShow) {
                newState.addCoDriver || newState.showMore ? hide(showMoreButton) : show(showMoreButton);
            } else {
                hide(showMoreButton);
            }
        };
        let toggleViaText = () => {
            let viaLastDatabaseEls = this.el.querySelectorAll(".login-via-text");
            for (let viaLastDatabaseEl of viaLastDatabaseEls) {
                viaLastDatabaseEl.textContent = newState.viaLastDatabase;
                newState.viaLastDatabase && (newState.stageTwoState || !newState.showMore) ? show(viaLastDatabaseEl) : hide(viaLastDatabaseEl);
            }
        };

        this.lastState = newState;

        if (document.getElementById("login-panel")) {
            let userEl = this.el.querySelector("#username") as HTMLInputElement;
            let passwordEl = this.el.querySelector("#password") as HTMLInputElement;
            this.updateLoginState(oldState, newState, userEl, passwordEl, username);

            this.checkIfInProgress(newState, tryShowHideMoreToggle, userEl, toggleViaText, passwordEl);
            this.updateQrLoginVisibility(newState);

            this.el.querySelector("#dashboard-debug-info").textContent = `${ (newState.androidAppVersion ? `${ newState.androidAppVersion }:` : "") }${ newState.appVersion }`;
        }
    }

    private checkIfInProgress(newState: ILoginState, tryShowHideMoreToggle: (isShow: boolean) => void, userEl: HTMLInputElement, toggleViaText: () => void, passwordEl: HTMLInputElement) {
        if (newState.isInProgress || (this.isAuthenticated)) {
            this.dispatchLoginRequired(LoginRequiredStatus.Default);

            let cancelButton = this.el.querySelector("#login-cancel");
            let progressTextEl = this.el.querySelector("#login-loading-text");

            if (progressTextEl) {
                progressTextEl.textContent = newState.progressText;
            }

            hide(this.el.querySelector("#login-form"));
            hide(this.el.querySelector("#login-form-auth"));
            hide(this.el.querySelector("#login-error-box"));
            tryShowHideMoreToggle(false);
            show(this.el.querySelector("#login-panel-process"));

            this.el.querySelector("#login-submit").disabled = true;

            this.spinner.spin(document.getElementById("spinner-container"));
            if (cancelButton) {
                newState.isCancelable ? show(cancelButton) : hide(cancelButton);
            }

            if (progressTextEl) {
                newState.isLogout ? hide(progressTextEl) : show(progressTextEl);
            }
        } else {
            let status: LoginRequiredStatus = LoginRequiredStatus.Default;
            let errorMessage: string | undefined;
            if (newState.error) {
                status = LoginRequiredStatus.Error;
                errorMessage = this.getString(newState.error);
            } else if (newState.addCoDriver) {
                status = LoginRequiredStatus.AddCoDriver;
            } else {
                status = LoginRequiredStatus.LoginRequired;
            }
            this.dispatchLoginRequired(status, errorMessage);

            this.spinner && this.spinner.stop();

            hide(this.el.querySelector("#login-panel-process"));
            if (newState.stageTwoState) {
                let passwordField = this.el.querySelector("#password-container");
                let submitButton = this.el.querySelector("#login-submit");
                tryShowHideMoreToggle(false);
                hide(this.el.querySelector("#login-form"));
                this.twoStageLoginSpinner && this.twoStageLoginSpinner.stop();
                if (newState.stageTwoState === StageTwoState.Checking) {
                    this.twoStageLoginSpinner.spin(document.getElementById("login-username-spinner-container"));
                    hide(passwordField);
                    hide(submitButton);
                } else {
                    show(passwordField);
                    show(submitButton);
                }
                show(this.el.querySelector("#login-form-auth"));
            } else {
                !newState.isExpiredSession && tryShowHideMoreToggle(true);
                show(this.el.querySelector("#login-form"));
                hide(this.el.querySelector("#login-form-auth"));
            }

            this.el.querySelector("#login-submit").disabled = false;

            if (errorMessage) {
                this.isAuthenticated = false;
                show(this.el.querySelector("#login-error-box"));
                this.el.querySelector("#login-error-text").textContent = errorMessage;
            } else {
                hide(this.el.querySelector("#login-error-box"));
            }

            let expiredLogoutButton = this.el.querySelector("#login-session-expired-logout");
            let showMoreFields = this.el.querySelector("#login-more");

            userEl.disabled = newState.isExpiredSession || false;
            newState.isExpiredSession || newState.loginDifferentDbError ? show(expiredLogoutButton) : hide(expiredLogoutButton);
            newState.addCoDriver || !newState.showMore ? hide(showMoreFields) : show(showMoreFields);

            toggleViaText();

            if (!newState.showMore) {
                if (newState.username) {
                    passwordEl.focus();
                } else {
                    userEl.focus();
                }
            }
        }
    }

    private authentication(username, password, database, server, abortableToken: AbortableToken) {
        return new CancelablePromise((resolve, reject) => {
            let getErrorMessage = function (errorObject) {
                let exceptionType = errorObject?.data?.type ?? null;
                let errorMessage = errorObject?.message?.replace("JSONRPCError: ", "") ?? "";

                if (exceptionType) {
                    switch (exceptionType) {
                        case "DatabaseVersionOldException":
                            errorMessage = "The database is under maintenance. Please try again later.";
                            break;
                        case "DbUnavailableException":
                            errorMessage = "Unknown database.";
                            break;
                        case "InvalidUserException":
                            if (errorObject.message.indexOf(DB_INPUT_REQUIRED_MSG) !== -1) {
                                errorMessage = DB_INPUT_REQUIRED_MSG;
                                break;
                            } else if (errorObject.message.indexOf(USER_LOCKOUT_MSG) !== -1) {
                                errorMessage = USER_LOCKOUT_MSG;
                                break;
                            }
                            errorMessage = "Incorrect username or password.";
                            break;
                        default:
                            break;
                    }
                }
                let statusDefined = errorObject.status !== null && typeof (errorObject.status) !== "undefined";

                if (statusDefined && errorObject.status === 0) {
                    errorMessage = LoginErrors.NETWORK_ERROR_MSG;
                } else if (statusDefined && errorObject.status === 404) {
                    errorMessage = LoginErrors.SERVER_UNREACHABLE_MSG;
                }

                return errorMessage;
            };
            let isComplete = false;
            let apiCall;

            apiCall = this.app.api.authenticate(
                server,
                database,
                username,
                password,
                () => {
                    apiCall = null;

                    if (!isComplete) {
                        isComplete = true;
                        if (!abortableToken.isAborted()) {
                            resolve();
                        }
                    }
                },
                errorObject => {
                    apiCall = null;
                    logger.error(logger.Source.USER, `Login error: ${ errorObject.message }`, username);
                    if (!isComplete) {
                        reject({
                            message: getErrorMessage(errorObject),
                            isAborted: false,
                            retryAttempts: 1
                        });
                        isComplete = true;
                    }
                }
            );

            abortableToken.onAbort(() => {
                apiCall && apiCall.abort();
                apiCall = null;
            });
        });
    }

    private navigateToNext(): CancelablePromise {
        endBasicLoginProfiler();
        let currentVehicle = getCurrentVehicle();
        let currentUser = this.app.userService.getCurrentUser();
        this.dispatchLoginRequired(LoginRequiredStatus.Default);
        if (this.addCoDriver) {
            let isDriver = currentUser && (currentUser.driverGroups || currentUser.keys);
            let device = getCurrentVehicle();

            if (isDriver) {
                return this.app.sync.synchronize(
                    false,
                    undefined,
                    [SyncType.HOS]
                ).then(() => {
                    return CancelablePromise.all(this.app.vehicleService.createLoginLogoffLogs(currentUser, device))
                    .then(() => {
                        let workflowState: IWorkflowState = { coDriver: true };
                        if (this.nextPage) {
                            workflowState.next = this.nextPage;
                        }
                        // WorkdayType set after workflow exits
                        this.app.router.toWorkflow(false, workflowState, this.skipWorkflow);
                    });
                });
            } else {
                currentUser && this.app.store.dispatch(DataActions.setWorkday({ [currentUser.id]: WorkdayType.Started }));
                this.app.router.navigateToPage(getHomeHash());
            }
        } else if (currentVehicle && (this.alreadyLoggedIn || this.isLoggingOut)) {
            this.alreadyLoggedIn = false;
            clearTempAssetsFromState(this.app.store, this.app.userService);
            if (this.nextPage) {
                // Push a history entry for the dashboard so the back button returns the user there
                window.history.pushState(null, "", `${ window.location.pathname }#${ getHomeHash() }`);
            }
            this.navigateToPage(this.nextPage || getHomeHash());
        } else {
            let workflowState: IWorkflowState = {};
            if (this.isReselectVehicle) {
                workflowState.changeVehicle = true;
            }
            if (this.nextPage) {
                workflowState.next = this.nextPage;
            }
            if (this.vehicleHint) {
                workflowState.vehicleHint = this.vehicleHint;
            }

            if (this.redirect) {
                workflowState.redirect = this.redirect;
            }

            currentUser && this.app.store.dispatch(DataActions.setWorkday({ [currentUser.id]: WorkdayType.Initializing }));
            let serialNumber = this.app.store.getState().data.currentHardwareId;
            let isIoxUsbAttached = this.app.store.getState().mobile.ioxAttached;
            if (isIoxUsbAttached && serialNumber) {
                const errorCallback = (errorMessage) => {
                    this.app.store.dispatch(DataActions.setCurrentHardwareId(undefined));
                    toastr.error(this.getString(errorMessage), undefined, undefined, { isUserNightMode: this.app.store.getState().data.nightMode });
                    this.app.router.toWorkflow(false, workflowState, this.skipWorkflow);
                };
                getDeviceBySerialNumber(
                    this.app.api,
                    serialNumber,
                    (result) => {
                        if (result[0]) {
                            let vehicleByHardwareId = new Device(result[0]);
                            workflowState.newDeviceId = vehicleByHardwareId.id;
                            this.app.router.toWorkflow(false, workflowState, this.skipWorkflow);
                        } else {
                            errorCallback("Device not found in this database. Please contact your administrator.");
                        }
                    },
                    () => {
                        errorCallback("Device could not be attached due to network issues.");
                    }
                );
            } else {
                this.app.router.toWorkflow(false, workflowState, this.skipWorkflow);
            }
        }
        return CancelablePromise.resolve();
    }

    private getLastDatabase(): string | null {
        let lastDatabase = this.app.store.getState().data.lastDatabase;
        return lastDatabase ? lastDatabase.toLowerCase() : null;
    }

    private getLastServer(): string {
        let lastServer = this.app.store.getState().data.lastServer;
        if (!lastServer) {
            return getCurrentHostName();
        }
        return lastServer;
    }

    private addLoginDurationEvent(loginStartTime: number) {
        let loginCompleteTime: number = getServerDate().getTime();
        let loginTotalTime: number = (loginCompleteTime - loginStartTime) / 1000;
        let connectionType: string | any = getConnectionType();

        logger.event(logger.Event.LOGINTIME, `Time taken to login: ${ loginTotalTime }s`, this.app.userService.getCurrentUser(), loginTotalTime, connectionType);
    }

    private dispatchDriverActionNecessary(isNecessary: boolean) {
        this.app.store.dispatch(MobileActions.setDriverActionNecessary({
            isNecessary: isNecessary,
            actionType: DriverActionTypes.Login
        }));
    }

    private dispatchLoginRequired(status: LoginRequiredStatus, errorMessage?: string) {
        this.app.store.dispatch(MobileActions.setLoginRequired({ status, errorMessage }));
    }

    private clearVehicles() {
        clearCurrentVehicle();
        // Remove previous vehicle. Makes sure the vehicle sync runs on next login.
        clearPreviousVehicle();
    }

    private navigateToPage(pageToNavigate, pageArgs?) {
        this.closeDrivingWarningDialog();
        this.app.router.navigateToPage(pageToNavigate, pageArgs);
    }

    private addLoginAuditLog(iamApi: IamApi, database: string) {
        iamApi.call("Add", {
            typeName: "Audit",
            entity: {
                name: "UserLogin",
                database
            }
        }, () => {
            // Ignore issue with login audit
        });
    }

    private hasAccessToVehicle() {
        let accessToDeviceCheckPromise: CancelablePromise<boolean>;
        let currentDevice = getCurrentVehicle();

        if (this.addCoDriver && currentDevice) {
            accessToDeviceCheckPromise = this.app.vehicleService.hasAccessToDevice(currentDevice);
        } else {
            accessToDeviceCheckPromise = CancelablePromise.resolve(true);
        }

        return accessToDeviceCheckPromise;
    }

    private startSession(): CancelablePromise<boolean> {
        return new CancelablePromise((resolve) => {
            this.app.session.start(this.translator, this.addCoDriver, hasError => {
                if (hasError) {
                    if (hasError.type === "DeclinedEula") {
                        this.showLoginForm();
                        resolve(false);
                    } else if (hasError.type === "InvalidLogin") {
                        // Rest of page will handle showing error
                        resolve(false);
                    } else {
                        this.showImproperPermissionMessage(hasError, this.lastState.username);
                        resolve(false);
                    }
                } else {
                    resolve(true);
                }
            });
        });
    }


    private getUserContext(credentials: ICredentials, server: string, iamSettings?: IIamSettings): CancelablePromise<boolean> {
        return new CancelablePromise((resolve) => {
            if (shouldAddExtraLogging(logger.Source.IAM)) {
                logger.log(logger.Source.IAM, `Login.getUserContext: Getting user context for user ${ credentials.userName }`);
            }
            this.app.context = this.app.manager.getContext(credentials.userName, {
                credentials,
                server,
                iamSettings
            });

            if (redirectDatabaseIfNeeded(this.app.context)) {
                resolve(false);
                return;
            }

            const handleSyncProgress = (type, itemsSynced, itemsToSync) => {
                this.showMessage(this.translator.getString`Synchronized ${ type } logs (${ itemsSynced.toString() } of ${ itemsToSync.toString() })...`);
                this.setIsCancelable(false);
            };
            const logSuccessfulAuthentication = () => {
                logger.event(
                    logger.Event.AUTHENTICATION,
                    `Successfully authenticated user ${ credentials.userName }. Current # of active users: ${ this.app.store.getState().activeUsers.length }`,
                    this.app.userService.getCurrentUser()
                );
            };
            let hasVehicle = !!getCurrentVehicle();

            return this.app.sync.synchronize(false, handleSyncProgress, [SyncType.UserHosRuleSet]).then(async () => {
                // before the background sync, since its calls on the old session would clear the new one after a password update
                await promptPasswordUpdateIfRequired(this.app, credentials);
                this.app.sync.synchronize(false, undefined, !hasVehicle ? [SyncType.Messages] : [SyncType.UserHosRuleSet], undefined, hasVehicle).then(() => {
                    logSuccessfulAuthentication();
                });

                this.dispatchDriverActionNecessary(false);
                this.navigateToNext();
                resolve(true);
            });
        });
    }

    private setLastDatabase(credentials: ICredentials) {
        if (credentials && credentials.database) {
            this.app.store.dispatch(DataActions.setLastDatabase(credentials.database.toLowerCase()));
        }
    }

    private setLastServer(server: string) {
        // Only set the geotabDrive_@store_data.lastServer field if it was entered directly in the form and is different than the current hostname
        let currentHostName = getCurrentHostName().toLowerCase();
        let enteredServer = server.replace(/\S*:\/\//, "").replace(/\/$/, "").toLowerCase();
        if (currentHostName !== enteredServer) {
            this.app.store.dispatch(DataActions.setLastServer(enteredServer));
        } else {
            this.app.store.dispatch(DataActions.clearLastServer());
        }
    }

    private setLastLoginDate(credentials: ICredentials) {
        if (!credentials.userName) {
            return;
        }
        this.app.store.dispatch(DataActions.setLastLoginDate({[credentials.userName]: getServerDate().toISOString()}));
    }

    private loginSuccess(
        credentials: ICredentials,
        server: string,
        lastServer: string | null,
        database: string,
        lastDatabase: string | null,
        iamSettings?: IIamSettings
    ) {
        let invalidLoginCallback = (errorMessage: string) => {
            this.app.api.forget(undefined, errorMessage);
            this.cancelLogin();
            return CancelablePromise.resolve(false);
        };

        // Hold on! We don't want to log this guy in at all now, do we?
        let cleanLastDatabase = lastDatabase?.toLowerCase();
        if (this.addCoDriver && (server !== lastServer || (cleanLastDatabase && database !== cleanLastDatabase))) {
            return invalidLoginCallback("You cannot add a driver from a different server or database.");
        }

        let loggedInUser = this.app.userService.getCurrentUser();
        if (loggedInUser && cleanLastDatabase && database !== cleanLastDatabase) {
            this.sessionUser = loggedInUser?.name;
            this.app.store.dispatch(DriveAPICredentialsActions.clearUserCredentials(sanitizeUserName(credentials.userName)));
            this.updateState({
                isInProgress: false,
                error: "You cannot add a driver from a different database.",
                isExpiredSession: false,
                loginDifferentDbError: true
            }, credentials.userName);
            this.cancelLogin();
            return CancelablePromise.resolve(false);
        }

        // cleanup error
        this.updateState({ error: "" });

        // Don't navigate away if the server is 127.0.0.1 (that means we're authenticated against the current server)
        if (server && server !== getCurrentHostName() && server !== "127.0.0.1") {
            logger.log(logger.Source.USER, `Login redirect to new server '${ server }' from '${ getCurrentHostName() }'`, credentials.userName);
            this.setLastDatabase(credentials);
            // We've changed servers, cap'n! Set sail for unknown lands! Unregister all service workers before leaving ...
            return unregisterServiceWorkers(undefined, () => true)
                .then(() => loginRedirect(server, credentials, this.nextPage, this.skipWorkflow), () => loginRedirect(server, credentials, this.nextPage, this.skipWorkflow))
                .then(() => CancelablePromise.resolve(false));
        }

        logger.log(logger.Source.USER, `Successfully authenticated against '${ server }'`, credentials.userName);
        this.isAuthenticated = true;

        let internalUsername = this.options?.state?.credentials?.userName;
        let externalUsername = this.options?.state?.credentials?.externalUsername;
        if (internalUsername && externalUsername) {
            this.app.manager.openCabService?.registerOpenCabUser(internalUsername, externalUsername);
        }

        return Promise.all([
            // Checks if the co-driver has access to the current vehicle (a no-op for regular logins)
            this.hasAccessToVehicle(),
            // Checks that rulesets are available in Redux cache, otherwise ensures we have them from the backend before proceeding
            this.hasHosRulesets()
        ])
        .then(([hasAccessToCurrentVehicle, hasRuleSets]) => {
            if (!hasAccessToCurrentVehicle) {
                this.showNoAccessToTheCurrentVehicleWarning();
                return false;
            } else if (!hasRuleSets) {
                return false;
            }
            return this.startSession();
        })
        .then(async isSessionStarted => {
            if (!isSessionStarted) {
                return false;
            }
            this.setLastDatabase(credentials);
            this.setLastLoginDate(credentials);
            this.setEnableSyncDelay(false);
            await registerPushTokenAfterLogin(this.app, this.app.store);
            // Hydrate a native session before getUserContext adds the user to
            // activeUsers (which triggers syncActiveUsers). The RN handler is a
            // no-op when a native session already exists (IAM / SAML paths that
            // go through the native auth bridge).
            // Basic-auth logins provide sessionId; webview IAM logins provide iamJwt.
            if (credentials.sessionId || credentials.iamJwt) {
                webViewLayer.sessionEstablished({
                    server,
                    database: credentials.database,
                    userName: credentials.userName,
                    sessionId: credentials.sessionId,
                    iamJwt: credentials.iamJwt,
                    oidcRefreshToken: credentials.oidcRefreshToken,
                    oidcIdToken: credentials.oidcIdToken,
                    oidcExpiresAt: credentials.oidcExpiresAt,
                    issuer: iamSettings?.loginUrl,
                    clientId: iamSettings?.clientId
                });
            }
            return this.getUserContext(credentials, server, iamSettings);
        })
        .catch((err) => {
            handleApi(err);
            return false;
        });
    }

    private doLogoutImpl(): void {
        let sessionCloseProfiler = PerformanceProfilerFactory.getInstance(MeasureName.closeSession);
        sessionCloseProfiler.start();
        void this.app.session.close(() => {
            sessionCloseProfiler.end();
            this.switchToNextUser();
        }, false);
    }

    private logout(): void {
        let logoutPerformanceProfiler = PerformanceProfilerFactory.getInstance(MeasureName.Logout);
        logoutPerformanceProfiler.start();
        this.setIsCancelable(true);
        this.showMessage("Logging out...");
        let loggingOutTimeout: number | undefined;
        let hasNetworkError = false;
        let syncProgressCallback = (type: string, itemsSynced: number, itemsToSync: number, errorObject, errorDict?) => {
            let networkError = isNetworkError(errorObject);
            if (!networkError && errorDict) {
                for (const key in errorDict) {
                    if (Object.prototype.hasOwnProperty.call(errorDict, key) && isNetworkError(errorDict[key])) {
                        networkError = true;
                        break;
                    }
                }
            }
            if (!networkError) {
                this.showMessage(this.translator.getString`Synchronized ${ type } logs (${ itemsSynced.toString() } of ${ itemsToSync.toString() })...`);
                this.setIsCancelable(false);
            } else {
                logger.error(logger.Source.ERROR, "logout - Network Error - syncProgressCallback");
                hasNetworkError = true;
            }
        };

        // It's the last sync! Unregister tasks, disable server events...
        // The process might have been canceled by this point, so we need to make sure it isn't before we turn off sync tasks.
        let currentUser: DeepReadonlyObject<IUser> | null = this.app.userService.getCurrentUser();
        if (currentUser && this.app && this.app.sync) {
            // If logout takes > 2 minutes, ask the user if he/she wants to cancel and try later
            loggingOutTimeout && clearTimeout(loggingOutTimeout);
            loggingOutTimeout = setTimeout(() => {
                this.showMessage("We're having some trouble logging you out. Please wait or try again later.");
                this.setIsCancelable(true);
            }, 2 * 60 * 1000); // 2 mins wait

            let logoffLog: DutyStatusLog | undefined;
            this.app.manager.disableSyncTasks(currentUser.name.toLowerCase());
            // MYG-147181 / MYG-147182 (logging only): open the logout race diagnostics window.
            startLogoutDiagnostics("legacy", currentUser);

            // HOS Sync needs to hit server first, but we can create the driver change first to store it locally
            this.executeLogoutAddins()
                .then(() => {
                    if (this.app.userService.getActiveUserNames().length === 1) {
                        addinsCache.clear();
                    }
                    return this.app.vehicleService.addLoginLogoffLog("Logoff", currentUser, getCurrentVehicle(), undefined, undefined, true);
                })
                .then(newLog => {
                    if (newLog) {
                        logoffLog = newLog;
                    }
                    const isKillswitchEnabled = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_CODRIVER_LOGOUT_VEHICLE_STATE_FIX);
                    const hasOtherActiveDrivers = this.app.userService.getActiveUserNames().length > 1;
                    if (!isKillswitchEnabled && hasOtherActiveDrivers) {
                        return CancelablePromise.resolve();
                    }
                    return this.app.vehicleService.setDriverVehicle(null, "UnknownDriverId");
                })
                .then(() => logoffLog ? this.app.sync.dutyStatusLogs.addItem(logoffLog, true) : CancelablePromise.resolve())
                .then(() => {
                    // MYG-147181 (logging only): tag diagnostics with the logout sync phase.
                    setLogoutDiagnosticsPhase("hosSync");
                    return this.app.sync.synchronize(false, syncProgressCallback, [SyncType.HOS]);
                })
                .then(() => {
                    if (hasNetworkError) {
                        return CancelablePromise.reject();
                    }
                    // MYG-147181 (logging only): the DriverChange sync below is logout's own final sync.
                    setLogoutDiagnosticsPhase("selectiveSync");

                    let selectiveSync = [
                        SyncType.DriverChange,
                        SyncType.DVIR,
                        SyncType.Messages,
                        SyncType.Shipments,
                        SyncType.TrailerAttachments,
                        SyncType.UserHosRuleSet,
                        SyncType.TripAnnotation,
                        SyncType.GroupLinkedDevice
                    ];
                    return this.app.sync.synchronize(false, syncProgressCallback, selectiveSync, undefined, undefined, true);
                })
                .then(() => CancelablePromise.resolve(cleanupOldDrvfsFiles(this.app.store)))
                .then(() => {
                    if (hasNetworkError) {
                        return CancelablePromise.reject();
                    }

                    clearTimeout(loggingOutTimeout);
                    logger.timeEnd(logger.Source.USER, "sync-logs-logout", "Syncing logs on logout", currentUser);
                    logoutPerformanceProfiler.end();
                    // MYG-147181 / MYG-147182 (logging only): close the logout race diagnostics window.
                    endLogoutDiagnostics("completed");
                    this.doLogoutImpl();
                    return;
                })
                .catch(() => {
                    clearTimeout(loggingOutTimeout);
                    logoutPerformanceProfiler.end();
                    this.cancelLogout(true, true);
                });

            logger.time(logger.Source.USER, "sync-logs-logout");
        } else {
            logoutPerformanceProfiler.end();
            this.doLogoutImpl();
        }
    }

    private switchToNextUser(expiredSession = false) {
        let activeUsers = this.app.userService.getUsers(true);
        if (activeUsers && activeUsers[0] && activeUsers[0].id) {
            // Switch to the next user down
            this.app.router.navigateToPage("login", { switchUser: activeUsers[0].id });
        } else {
            this.clearVehicles();
            this.app.store.dispatch(DataActions.clearAllActiveEvents());
            this.app.store.dispatch(MobileActions.setEldMalfunction(false));
            this.app.store.dispatch(MobileActions.setEldDiagnostic(false));
            this.app.store.dispatch(DeviceMovementActions.setStateUpdate({
                isDriving: false,
                source: "Login",
                loggingMessage: "Login switch to next user "
            }));
            this.app.store.dispatch(ShipmentActions.clearAllShipments());
            this.app.router.navigateToPage("login");
            if (!expiredSession && !isTestDomain(window.location.origin)) {
                removeNumberFromSubDomainPath(geotab.location.getVendorPrefix());
            }
        }
    }

    private getDatabaseNameFromUriAndLog() {
        let dbName = getDatabaseNameFromUri(window.location);
        if (dbName !== ""){
            logger.event(logger.Event.AUTHENTICATION, `Database field autofilled using URI: ${ dbName } - ${ this.el.querySelector("#database")?.value.trim().toLowerCase() }`);
        }
        return dbName;
    }

    private setIsCancelable(isCancelable: boolean) {
        this.updateState({ isCancelable });
    }

    /**
     * MYG-151497: QR sign-in for the Tesla Semi IVI. Offered only when all of these hold, so nobody else sees it:
     * the Tesla in-vehicle browser, the server feature flag, a browser and host the IAM login itself accepts, and a
     * realm the Tesla client belongs to. Anything else leaves the login page exactly as it is.
     */
    private async mountQrLogin() {
        this.qrLoginPanel?.destroy();
        this.qrLoginPanel = undefined;
        const mountId = ++this.qrMountId;

        if (!this.isQrLoginPossible()) {
            return;
        }

        let config: IDeviceAuthConfig;
        try {
            config = await this.getQrConfig();
        } catch (error) {
            logger.log(logger.Source.IAM, `QR device login is not offered: ${ (error as Error)?.message || String(error) }`);
            return;
        }

        const container = this.el.querySelector("#login-panel .panel_padding");
        if (mountId !== this.qrMountId || !container || !isQrLoginIssuerSupported(config.issuer)) {
            return;
        }

        this.qrLoginPanel = new QrLoginPanel({
            getConfig: () => this.getQrConfig(),
            onTokens: (tokens, tokenConfig) => void this.completeQrLogin(tokens, tokenConfig),
            onError: error => this.logQrLoginError(error),
            translate: key => this.translator.getLocalTranslation(key)
        });
        container.appendChild(this.qrLoginPanel.el);
        this.updateQrLoginVisibility(this.lastState);
    }

    private isQrLoginPossible(): boolean {
        if (this.addCoDriver || this.isLoggingOut || !isQrLoginEnabled(navigator.userAgent)) {
            return false;
        }

        if (!this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_TESLA_QR_LOGIN)) {
            return false;
        }

        // The IAM login falls back to the legacy login on old apps and browsers, and moves a numbered host to its
        // canonical host before it calls the identity provider (whose allowed origins list that host). The QR option
        // calls the identity provider from this page, so it must not appear where the IAM login would not.
        const chromeVersion = this.getChromeVersion();
        const safariVersion = this.getSafariVersion();
        if (getAppType() === AppType.CordovaApp
            || (chromeVersion < constants.IAM.MINIMUM_CHROME_VERSION && chromeVersion !== -1)
            || (safariVersion < constants.IAM.MINIMUM_SAFARI_VERSION && safariVersion !== -1)) {
            return false;
        }

        const urlParts = getUrlPrefix(getCurrentHostName().split("."));
        return !(isNumberFromSubDomainPathNotAllowed(geotab.location.getVendorPrefix()) && urlParts.prefixNumber !== "");
    }

    /** The realm and client for the device grant. Read once per view: the server answers the same each time. */
    private getQrConfig(): Promise<IDeviceAuthConfig> {
        if (!this.qrConfigPromise) {
            const promise = getDeviceAuthConfig(this.app.api, getCurrentHostName(), QR_CLIENT_ID);
            this.qrConfigPromise = promise;
            promise.catch(() => {
                this.qrConfigPromise === promise && (this.qrConfigPromise = undefined);
            });
        }
        return this.qrConfigPromise;
    }

    private logQrLoginError(error: unknown) {
        const code = error instanceof DeviceAuthError ? error.code : "unknown";
        const message = `QR device login did not complete (${ code }): ${ (error as Error)?.message || String(error) }`;
        // A code that expired, a request denied on the phone and a lost connection (common in a moving vehicle) are
        // normal outcomes, not faults.
        const isExpected = code === DeviceAuthErrorCode.Expired || code === DeviceAuthErrorCode.Network || code === "access_denied";
        isExpected ? logger.log(logger.Source.IAM, message) : logger.error(logger.Source.IAM, message);
    }

    // The QR option belongs to the first step only: hide it while a login is running, on the password
    // step and on an expired-session screen (which must be re-authenticated as the same user).
    private updateQrLoginVisibility(state: ILoginState) {
        this.qrLoginPanel?.setVisible(!state.isInProgress && !this.isAuthenticated && !state.stageTwoState && !state.isExpiredSession);
    }

    /**
     * Whether Drive IAM sign-in is allowed for this user. Every other IAM login asks the server first
     * (GetUserRedirectInformationDrive), which is where the server's Drive-IAM switches and SAML routing apply;
     * a token alone does not carry them.
     */
    private async assertQrLoginAllowed(username: string, database: string) {
        const result = await this.app.api.serverCall(getCurrentHostName(), "GetUserRedirectInformationDrive", { database, userName: username });
        if (result?.informationType !== "IAM") {
            throw new QrLoginNotAllowedError();
        }
    }

    private async completeQrLogin(tokens: IDeviceAuthTokens, config: IDeviceAuthConfig) {
        const completion = ++this.qrCompletion;
        const run = ++this.qrLoginRun;
        const isCancelled = () => run !== this.qrLoginRun;
        const isSuperseded = () => completion !== this.qrCompletion;
        // IamFlow rewrites the address bar when it starts; a failed attempt must leave it as the driver had it, so a
        // retry keeps the database in the path.
        const returnUrl = `${ window.location.pathname }${ window.location.search }${ window.location.hash }`;
        let isIamFlowStarted = false;
        // The same database the username form would use: what was typed under Advanced, else the address. Read it
        // before showMessage(): that re-renders the form, which can put the previous value back in the field.
        const database = this.el.querySelector("#database")?.value.trim().toLowerCase() || this.getDatabaseNameFromUriAndLog();

        this.showMessage(this.getLoginAsMessage());

        try {
            const appState: IIamAppState = { database, server: getCurrentHostName(), addCoDriver: false };
            const { user, username, iamSettings } = createDeviceAuthSession(tokens, {
                issuer: config.issuer,
                clientId: config.clientId,
                language: this.app.translationService.getLanguage() || "en",
                appState
            });

            await this.assertQrLoginAllowed(username, database);
            if (isCancelled()) {
                throw new IamLoginCancelledError();
            }

            isIamFlowStarted = true;
            await new IamFlow(
                this.app,
                iamSettings,
                username,
                appState,
                false,
                this.addLoginAuditLog.bind(this),
                this.setCredentialsFromRemote.bind(this),
                this.updateState.bind(this),
                this.cancelLogin.bind(this),
                ((cred: ICredentials) => this.setLastDatabase(cred)),
                ((ser: string) => this.setLastServer(ser)),
                (() => this.showMessage(this.getLoginAsMessage()))
            // After its last check the login cannot be cancelled any more, so stop offering Cancel rather than offer
            // a button that would be ignored.
            ).loginWithUser(user, isCancelled, () => this.setIsCancelable(false));
            logger.log(logger.Source.IAM, "QR device login signed in", username);
        } catch (error) {
            // A call that failed after Cancel is a cancellation, whatever it failed with.
            const wasCancelled = error instanceof IamLoginCancelledError || isCancelled();
            // IamFlow signs out and revokes when it fails on its own. Everything else leaves live tokens behind.
            (!isIamFlowStarted || wasCancelled) && void revokeDeviceAuthTokens(config, tokens);

            // A newer approval (or removing the view) owns the page, the address and the panel now.
            if (isSuperseded()) {
                return;
            }

            window.history.replaceState({}, document.title, returnUrl);
            this.qrLoginPanel?.reset();
            this.setIsCancelable(true);

            if (wasCancelled) {
                return;
            }

            const failure = this.getQrLoginFailure(error);
            // IamFlow has already logged its own failures.
            if (!isIamFlowStarted || failure.isExpected) {
                const message = `QR device login failed: ${ (error as Error)?.message || String(error) }`;
                failure.isExpected ? logger.log(logger.Source.IAM, message) : logger.error(logger.Source.IAM, message);
            }
            this.updateState({ isInProgress: false, ...failure.state });
        }
    }

    /** What to show for a failed completion, and whether the failure is an ordinary outcome rather than a fault. */
    private getQrLoginFailure(error: unknown): { state: Partial<ILoginState>; isExpected: boolean } {
        const message = (error as Error)?.message;

        // The server says so when the user belongs to several databases (or none it can name) and none was given. The
        // username form handles it the same way: open Advanced and ask for the database.
        if (message === DB_INPUT_REQUIRED_MSG || (error instanceof IamDatabaseSelectionError && error.reason === "several")) {
            return { state: { showMore: true, databaseRequired: true, error: DB_INPUT_REQUIRED_MSG }, isExpected: true };
        }

        if (message === USER_NOT_VERIFIED_VIA_IAM) {
            return { state: { showMore: true, error: "Account not verified" }, isExpected: true };
        }

        if (error instanceof IamDatabaseSelectionError) {
            // No database at all is a different problem, which a database field cannot fix.
            return { state: { error: QR_NO_DATABASE_MSG }, isExpected: true };
        }

        if (error instanceof QrLoginNotAllowedError) {
            return { state: { error: QR_NOT_ALLOWED_MSG }, isExpected: true };
        }

        return { state: { error: EXTERNAL_AUTH_FAILED_MSG }, isExpected: false };
    }

    private showMessage(task: string | CancelablePromise<string>, isLogout = false) {
        let translatePromise: CancelablePromise<string> = typeof (task) === "string" ? this.translator.translate(task) : task;

        this.updateState({
            progressText: "...",
            isInProgress: true,
            isLogout: isLogout
        });

        void translatePromise.then(message => this.updateState({ progressText: message }));
    }

    private openDrivingWarningDialog() {
        let callback = () => {
            this.app.screenHandler.canClearLockScreen(true).then(isVechicleStoppedResult => {
                if (!isVechicleStoppedResult) {
                    toastr.info(DRIVING_WITHOUT_LOGIN_WARNING_MESSAGE);
                } else {
                    this.closeDrivingWarningDialog();
                    this.app.store.dispatch(DeviceMovementActions.setStateUpdate({
                        isDriving: false,
                        source: "USER_EVENT",
                        loggingMessage: "User manually clear Lockscreen from login"
                    }));
                    return;
                }
            });
        };

        this.positionDialog = new Dialog(
            "position-dialog",
            "Stop safely, then log in to record your driving time.",
            this.translator,
            "Vehicle in motion",
            [
                {
                    id: "position-dialog-cancel",
                    text: "Cancel",
                    callback: callback
                }
            ],
            {
                isNightMode: this.app.store.getState().data.nightMode,
                showCloseIcon: false,
                closable: false
            }
        );
        void this.positionDialog.open();
    }

    private closeDrivingWarningDialog() {
        if (this.positionDialog) {
            this.positionDialog.remove();
            this.positionDialog = null;
        }
    }

    private showLoginForm() {
        this.updateState({ isInProgress: false });
    }

    private switchUser(state) {
        // Start switch user process
        let userId = state.switchUser;
        let allUsers = this.app.userService.getUsers(false);
        let thisUser = new User(this.findById(allUsers, userId));
        let newUserName: string | null = thisUser.name;

        if (thisUser && newUserName) {
            logger.log(logger.Source.USER, `Legacy switchUser: Before context switch, vehicle: ${ getCurrentVehicle()?.id || "null" }`);
            // Check if the user has been deleted
            this.app.context = this.app.manager.getContext(newUserName);
            logger.log(logger.Source.USER, `Legacy switchUser: After context switch, before render, vehicle: ${ getCurrentVehicle()?.id || "null" }`);
            this.app.render();
            logger.log(logger.Source.USER, `Legacy switchUser: After render, vehicle: ${ getCurrentVehicle()?.id || "null" }`);
            this.app.network.checkBridge();

            this.checkIfUserExists(userId)
                .then(() => {
                    this.app.session.updateSecurityClearances();
                    return this.app.sync.synchronize();
                })
                .then(() => {
                    if (this.isReselectVehicle) {
                        let nextPageState: IWorkflowState = {
                            isLogout: false,
                            step: "selectVehicle",
                            changeVehicle: true
                        };

                        if (state.newDeviceId) {
                            nextPageState.newDeviceId = state.newDeviceId;
                        }

                        this.navigateToPage("workflow", nextPageState);
                    } else {
                        if (this.nextPage) {
                            // Push a history entry for the dashboard so the back button returns the user there
                            window.history.pushState(null, "", `${ window.location.pathname }#${ getHomeHash() }`);
                        }
                        this.navigateToPage(this.nextPage || getHomeHash());
                    }
                    this.app.store.dispatch(MobileActions.setLastViewedLog(undefined));
                })
                .catch(deletedId => {
                    logger.log(logger.Source.USER, `The application was unable to switch to user '${ newUserName } (${ deletedId })', logging out.`, newUserName);

                    // If API call succeeds but user is not found, assume they must have been deleted. Forget the api.
                    scheduler.unregisterAll(newUserName as string);
                    this.app.api.forget();
                    this.displaySessionExpiredPage(newUserName as string);
                });
        }

        this.setIsCancelable(false);
        let fullName = thisUser && thisUser.getFullName();
        if (fullName) {
            this.showMessage(this.translator.getString`Switching to ${ fullName }`);
        } else {
            this.showMessage(`Switching to new driver...`);
        }
    }

    private async databaseRedirectIfRequired(credentials: ICredentials, server: string, iamSettings?: IIamSettings) {
        // Skip early redirect for cross-db codriver attempts - let loginSuccess handle validation
        let currentUser = this.app.userService.getCurrentUser();
        let lastDatabase = this.getLastDatabase();

        if (currentUser && lastDatabase && credentials.database) {
            let incomingDb = credentials.database.toLowerCase();
            let currentDb = lastDatabase.toLowerCase();

            if (incomingDb !== currentDb) {
                return false;
            }
        }

        // Check if we need to early redirect to another database
        // we need to create a temporary context to make the API call to get the user
        // if it's successful, we need to set the user, driveAPICredentials and activeUser store
        // so after the redirect, the we can fetch the correct user.
        if (!checkIfRedirectIsRequired(credentials.database, this.app.store)) {
            return false;
        }

        try {
            this.app.context = this.app.manager.getTempContext({ credentials: credentials, server, iamSettings });
            let user = (await this.app.context.api.callPromise("Get", {
                typeName: "User",
                search: { name: credentials.userName }
            }))[0];

            if (!user) {
                return false;
            }

            this.app.userService.setUser(user);
            this.app.context = this.app.manager.getContext(credentials.userName, {
                credentials: credentials,
                server: server,
                iamSettings
            });

            logger.log(logger.Source.USER, "Early redirecting to database", credentials.userName);
            this.setLastServer(server);
            databaseRedirect(credentials.database);

            return true;
        } catch (error) {
            // Ignore error and continue through the old login flow
            logger.error(logger.Source.ERROR, `Error during early redirect on login: ${ error }`);
        }

        return false;
    }

    private async setCredentialsFromRemote(state: ILoginUrlState, iamSettings?: IIamSettings) {
        let credentialsLoginPerformanceProfiler = PerformanceProfilerFactory.getInstance(MeasureName.CredentialsLogin);
        let credentialsFromRemote = state.credentials;
        let serverNameFromState = state.server ? state.server.toLowerCase() : "";
        let serverNameFromRemote = serverNameFromState || (credentialsFromRemote && (credentialsFromRemote as any).server ? (credentialsFromRemote as any).server : undefined);
        let currentHost = window.location.host;
        let serverNameOrCurrent = serverNameFromRemote || currentHost;
        let getSession = (server = "ThisServer") => {
            if (server !== "ThisServer" && credentialsFromRemote) {
                this.app.api.setUser(credentialsFromRemote, server, iamSettings);
            }

            return this.getSession().then(isLoginSucceeded => {
                credentialsLoginPerformanceProfiler.end();
                return isLoginSucceeded;
            });
        };

        if (!credentialsFromRemote) {
            return;
        }

        // check if early redirect is required
        // if it is return and do not continue with the login process
        if (state.credentials && state.server && await this.databaseRedirectIfRequired(state.credentials, state.server, iamSettings)) {
            return;
        }

        credentialsLoginPerformanceProfiler.start();

        // Create the temporary context until we verify the credentials' validity
        this.app.context = this.app.manager.getTempContext({ credentials: credentialsFromRemote, server: serverNameOrCurrent, iamSettings });

        this.app.api.callPromise("GetVersion", {})
            .then(() => getSession(serverNameFromRemote))
            .catch(() => {
                logger.log(logger.Source.USER, `Attempted to log into '${ serverNameFromRemote }, but no response from the server or it's not a MyGeotab server`);
                // The server isn't responding or is not a MyGeotab server
                // If so, switch the context to the local server and allow the user to log in
                this.app.api.forget();
                this.updateState({ error: "Could not connect to server. Please log in again.", isInProgress: false });
            });

        this.showMessage(this.getLoginAsMessage(credentialsFromRemote && credentialsFromRemote.userName));
    }

    private storeListener() {
        let isDriving = this.app.store.getState().data.driving;

        if (!this.positionDialog && isDriving && !this.app.userService.hasLoggedInUser()) {
            this.openDrivingWarningDialog();
        }
    }

    private displaySessionExpiredPage(apiUserName: string, currentUser = this.app.userService.getCurrentUser()) {
        if (currentUser) {
            this.sessionUser = apiUserName;
            this.updateState({
                isInProgress: false,
                error: this.getString("Your session has expired. You will need to log in again."),
                username: apiUserName,
                isExpiredSession: true
            });
        }
    }

    private checkIfUserExists(userId: string | null): CancelablePromise {
        return new CancelablePromise((resolve, reject) => {
            let creds = this.app.api.getCurrentCredentials();
            if (!creds) {
                reject(userId);
                return;
            }

            this.app.api.call(
                "Get",
                {
                    typeName: "User",
                    search: { id: userId }
                },
                users => {
                    if (users.length === 0) {
                        reject(userId);
                    } else {
                        resolve();
                    }
                },
                errorObject => {
                    // Handle the error, but continue
                    handleApi(errorObject);
                    resolve();
                }
            );
        });
    }

    private showNoAccessToTheCurrentVehicleWarning() {
        let doesNotHaveAccessDialog = new Dialog(
            "codriver-noaccess-dialog",
            "The user does not have access to the current device. Please contact your Administrator.",
            this.translator,
            "Improper Permissions",
            [{
                id: "codriver-noaccess-ok",
                text: "OK",
                callback: () => {
                    doesNotHaveAccessDialog.remove();
                    this.showLoginForm();
                }
            }],
            {
                closable: false,
                showCloseIcon: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );

        void doesNotHaveAccessDialog.open();
    }

    private getLoginAsMessage(userName?: string) {
        if (userName) {
            return this.translator.getString`Logging in as ${ userName }...`;
        } else {
            return "Logging in...";
        }
    }

    private onSamlCredentialsSuccess(credentialsString: string, username: string, database: string, server: string, callback: (credentials: ICredentialsWithServer) => void) {
        let auth = JSON.parse(credentialsString) as ICredentialsWithServer | string;
        if (typeof (auth) === "string") {
            // TODO: The JSON string retrieved from the MobileSDK (at least on Android) are double-escaped
            // Calling JSON.parse() again works, and will give us a proper object if the type of the original attempt is still a string
            auth = JSON.parse(auth) as ICredentialsWithServer;
        }
        auth.credentials = auth.credentials || {};
        if (!auth.credentials.userName) {
            auth.credentials.userName = username;
        }

        if (!auth.credentials.database) {
            auth.credentials.database = database;
        }

        if (!auth.server) {
            auth.server = server;
        }
        callback(auth);
    }

    private shouldLoginWithAS() {
        return window.geotabModules?.sso && (typeof window.geotabModules.sso["samlLoginWithAS"] === "function");
    }

    private samlLogin(samlLoginUrl: string, callback: (err?: Error, result?: string) => void, database?: string) {
        if (window.geotabModules?.sso) {
            if (this.shouldLoginWithAS()) {
                return window.isFeatureFlagsEnabled([FeatureFlagName.ENABLE_FORCE_USE_EPHEMERAL_FOR_SAML_LOGIN_WITH_AS], this.app.store, database)
                    .then(isEphemeralSessionEnabled => {
                        window.geotabModules?.sso.samlLoginWithAS({
                                samlLoginUrl: samlLoginUrl,
                                ephemeralSession: isEphemeralSessionEnabled?.[0]?.isEnabled || this.app.userService.getUsers().length > 0 || this.addCoDriver
                        }, callback);
                    });
            } else {
                return window.geotabModules.sso.samlLogin({ samlLoginUrl: samlLoginUrl }, callback);
            }
        }
    }


    private openSamlWindow(samlRedirectUrl: string, username: string, database: string, server: string) {
        let onEarlyExit = () => {
            this.updateState({ error: EXTERNAL_AUTH_FAILED_MSG });
            this.cancelLogin();
        };

        // MobileSDK handling of SAML window
        if (window.geotabModules?.sso) {
            this.samlLogin(samlRedirectUrl, (error, credentialsString) => {
                if (error) {
                    onEarlyExit();
                    handleApi(error, true);
                    return;
                }
                try {
                    this.onSamlCredentialsSuccess(credentialsString as string, username, database, server, credentials => {
                        this.setLastServer(server);
                        this.app.store.dispatch(DataActions.setLastUser(username));
                        let params = { ...credentials };
                        this.app.router.navigate(`login,${ rison.encode(params) }`, true);
                    });
                } catch (e) {
                    handleApi(e, true);
                    onEarlyExit();
                }
            }, database);
            return;
        }

        // Cordova + in-browser handling of SAML windows
        const win = openExternalUrl(samlRedirectUrl, "iab");

        let tryGetCredentials = () => {
            win.executeScript({ code: "sessionStorage.getItem('geotab_sso_credentials')" }, values => {
                if (values && values[0]) {
                    let authString = values[0];
                    try {
                        this.onSamlCredentialsSuccess(authString as string, username, database, server, credentials => {
                            this.setLastServer(server);
                            this.app.store.dispatch(DataActions.setLastUser(username));
                            win.removeEventListener("exit", onEarlyExit);
                            win.close();
                            this.app.router.navigate(`login,${ rison.encode(credentials) }`, true);
                        });
                    } catch {
                        // Close before we can get a value and navigate
                        win.close();
                        return;
                    }
                } else {
                    setTimeout(tryGetCredentials, 100);
                }
            });
        };

        if (win) {
            win.addEventListener("exit", onEarlyExit);
            win.addEventListener("loaderror", () => {
                win.close();
            });
            win.addEventListener("loadstop", tryGetCredentials);
        }
    }

    private wasDebugModeToggled() {
        const serverVal: string = this.el.querySelector("#server")?.value.trim().toLowerCase() ?? "";
        if (serverVal.indexOf(DRIVE_DEBUG_FLAG) === 0) {
            switch (serverVal.replace(DRIVE_DEBUG_FLAG, "")) {
                case DebugFlags.DebugModeOn:
                    this.app.store.dispatch(DataActions.setDebugMode(true));
                    break;
                case DebugFlags.DebugModeOff:
                    this.app.store.dispatch(DataActions.setDebugMode(false));
                    break;
                case DebugFlags.NewLoginPage:
                    this.app.router.navigate("#/-/login", true);
                    break;
                default:
                    break;
            }
            return true;
        }
        return false;
    }

    private setEnableSyncDelay(rescheduleTask: boolean) {
        if (this.app.store.getState().data.enableSyncDelay) {
            logger.log(logger.Source.USER, "login: skipped setting enableSyncDelay", this.app.userService.getCurrentUser());
            return;
        }

        this.app.store.dispatch(DataActions.setEnableSyncDelay(true));
        logger.log(logger.Source.USER, "login: enableSyncDelay=true", this.app.userService.getCurrentUser());
        if (rescheduleTask) {
            let overrideInterval = window.DRIVE_DEBUG?.STARTING_INTERVAL || Math.floor((Math.random() * 5 * 60 + 30) * 1000);
            scheduler.rescheduleTask("@sync", overrideInterval);
        }
    }
}
